<template>
	<AppContent ref="content" class="content">
		<header class="streaming-charts-header">
			<!-- logo -->
			<RouterLink :to="{ name: 'app.streaming-charts' }" class="logo">
				<picture>
					<source type="image/webp" :srcset="logoSrc.webp" />
					<img loading="lazy" :src="logoSrc.png" alt="JustWatch" />
				</picture>

				<div class="logo__title">Streaming Charts</div>
			</RouterLink>

			<!-- filter buttons -->
			<div class="nav">
				<div class="nav__filterbar">
					<HiddenHorizontalScrollbar :itemAmount="3" :hideNav="true" :fadedEdge="true">
						<FilterSelect
							:items="selectFiltersObjectTypes"
							:value="filterTitleObjectType"
							@input="onObjectTypeChange"
						>
							<template #icon>
								<FontAwesomeIcon :icon="faFilm" />
							</template>
						</FilterSelect>

						<FilterSelect :items="selectFiltersCountries" :value="filterCountry" @input="onCountryChange">
							<template #icon>
								<FontAwesomeIcon :icon="faGlobeAmericas" />
							</template>
						</FilterSelect>

						<!-- <StreamingChartsDatePicker
							:defaultCategory="route.query.ct"
							:defaultDate="route.query.d"
							@onSelectionChange="onSelectionChange($event)"
						/> -->
						<FilterSelect
							:items="selectFiltersCategories"
							:value="filterCategory"
							@input="onCategoryChange"
						>
							<template #icon>
								<FontAwesomeIcon :icon="faCalendar" />
							</template>
						</FilterSelect>
					</HiddenHorizontalScrollbar>
				</div>

				<RouterLink :to="{ name: 'app.titles.popular.list' }" class="nav__link">
					<BasicButton class="nav__link__button" color="dark">
						<span>{{ t('WEBAPP_STREAMING_GUIDE') }}</span>
						<FontAwesomeIcon :icon="faArrowRight" class="nav__link__button__icon" />
					</BasicButton>
				</RouterLink>
			</div>
		</header>

		<div class="charts">
			<div class="charts__top">
				<div class="divider">
					<div class="divider__line"></div>
				</div>

				<div v-if="firstTitle != null" class="claim">
					<div class="claim__content">
						<div class="claim__content__title">
							{{ getHeroSpinningText(firstTitle) }}
						</div>

						<div class="claim__content__rank">
							<SingleRankingDisplay
								v-if="firstTitle?.streamingChartInfo?.rank"
								:rank="firstTitle.streamingChartInfo.rank"
								:trend="firstTitle.streamingChartInfo.trend"
								:trendDifference="firstTitle.streamingChartInfo.trendDifference"
								:daysInTop1000="firstTitle.streamingChartInfo.daysInTop1000"
								:runtime="firstTitle.node.content.runtime ?? undefined"
								:releaseYear="firstTitle.node.content.originalReleaseYear ?? undefined"
								:withCondensed="true"
							/>
						</div>

						<div class="claim__content__watch">
							<WatchNowButtonNew
								color="primary"
								class="claim__content__watch__btn"
								:title="firstTitle.node"
								:uniqueProviders="true"
								size="slim"
							/>

							<RouterLink :to="firstTitle?.node.content.fullPath">
								<button class="claim__content__watch__details">
									{{ t('WEBAPP_MORE_DETAILS') }}
								</button>
							</RouterLink>
						</div>
					</div>

					<div class="claim__poster">
						<PosterGraphql
							:key="firstTitle.node.id"
							:title="firstTitle.node"
							imageProfile="poster_detail"
							:lazy="true"
							:showAvailabilityBadge="false"
						/>
					</div>
				</div>

				<UseArticleList
					v-if="firstTitle"
					:page-size="1"
					:page="1"
					:entity-id="firstTitle.node.id"
					v-slot="{ articles, totalCount }"
				>
					<div v-if="totalCount > 0" class="news">
						<div class="divider">
							<div class="divider__title">{{ t('WEBAPP_NEWS_ARTICLES') }}</div>
							<div class="divider__line"></div>
						</div>

						<RouterLink
							:to="article.content.fullPath"
							v-for="(article, index) in articles"
							:key="article.content.fullPath"
							class="news__article"
						>
							<GuideItemCondensed :article="article" :index="index" />
						</RouterLink>
					</div>
				</UseArticleList>
			</div>

			<div class="description">
				<div class="description__main">
					{{ t('WEBAPP_STREAMING_CHARTS_TITLE') }}
				</div>

				<div class="description__text">
					<p>
						{{ t('WEBAPP_STREAMING_CHARTS_TEXT') }}
					</p>
				</div>
			</div>

			<div class="charts__list">
				<div>
					<StreamingChartsList
						:titles="streamingChartTitles"
						:totalTitles="streamingChartData?.totalCount || 0"
					/>

					<div class="charts__list__pagination">
						<div class="share-buttons">
							<p class="share-buttons__text">{{ t('WEBAPP_SHARE_IT_ON') }}</p>

							<div class="share-buttons__container">
								<div class="share-buttons__button" @click="share(firstTitle, ShareMethod.FACEBOOK)">
									<FontAwesomeIcon :icon="faFacebook" />
								</div>

								<div class="share-buttons__button" @click="share(firstTitle, ShareMethod.TWITTER)">
									<FontAwesomeIcon :icon="faTwitter" />
								</div>

								<div class="share-buttons__button" @click="share(firstTitle, ShareMethod.CLIPBOARD)">
									<FontAwesomeIcon :icon="faCopy" />
								</div>
							</div>
						</div>

						<p class="charts__list__pagination__count">
							{{ t('WEBAPP_RESULTS') }} {{ streamingChartTitles?.length }}/{{
								streamingChartData?.totalCount
							}}
						</p>
					</div>

					<BasicButton
						color="primary"
						class="charts__list__pagination__button"
						:blocked="true"
						:disabled="loading || streamingChartData?.totalCount === streamingChartTitles?.length"
						@click="onLoadMore"
						>{{ t('WEBAPP_LOAD_MORE_TITLES') }}</BasicButton
					>
				</div>
			</div>

			<StreamingChartContactForm mailto="partner@justwatch.com">
				<template #heading>{{ $t('WEBAPP_SC_CONTACTFORM_TITLE') }}</template>
				<template #subheading> {{ $t('WEBAPP_SC_CONTACTFORM_TEXT') }}</template>
			</StreamingChartContactForm>
		</div>
	</AppContent>
</template>

<script setup lang="ts">
import { ModuleContentTitleTrend, PopularityRankTypes, StreamingChartObjectType } from '@/@types/graphql-types';
import WatchNowButtonNew from '@/components/buttons/WatchNowButtonNew.vue';
import PosterGraphql from '@/components/poster/PosterGraphql.vue';
import SingleRankingDisplay from '@/components/streaming-charts/SingleRankingDisplay.vue';
import StreamingChartsList from '@/components/streaming-charts/StreamingChartsList.vue';
import FilterSelect from '@/components/streaming-charts/ui/FilterSelect.vue';
import StreamingChartContactForm from '@/pages/landing-pages/streaming-charts/StreamingChartContactForm.vue';
import GuideItemCondensed from '@/pages/landing-pages/streaming-charts/GuideItemCondensed.vue';
import HiddenHorizontalScrollbar from '@/components/HiddenHorizontalScrollbar.vue';
// import StreamingChartsDatePicker from '@/pages/landing-pages/streaming-charts/StreamingChartsDatePicker.vue';
import { WebLocale } from '@/enums/web-locale';
import UseArticleList from '@/features/editorial/composables/useArticleList';
import { addToast } from '@/helpers/vfm/toast-helper';
import BasicButton from '@/ui-components/BasicButton.vue';
import { computed, ref } from 'vue';
import { ShareMethod } from '@/enums/share-methods';

import { faTwitter, faFacebook } from '@fortawesome/free-brands-svg-icons';
import { faCalendar, faCopy } from '@fortawesome/free-regular-svg-icons';
import { faArrowRight, faFilm, faGlobeAmericas } from '@fortawesome/free-solid-svg-icons';
import { WEB_LOCALES_EMOJIS } from '@/constants/web-locales.constant';
import {
	reverseCategoryQueryMapping,
	reverseObjectTypeQueryMapping,
} from '@/components/details/streaming-chart/streaming-chart-config';
import type { GetStreamingChartInfoQuery } from '@/components/graphql/queries/GetStreamingChartInfo.query';
import { useStreamingCharts } from '@/features/streaming-charts/composables/useStreamingCharts';
import { ClassicUrl, safeOpen } from '@/helpers/clickout-helper';
import { useLanguageStore } from '@/stores/pinia/useLanguageStore';
import { useRoutingStore } from '@/stores/pinia/useRoutingStore';
import { jw_changeLocale } from '@/routing';

const languageStore = useLanguageStore();
const routingStore = useRoutingStore();
const { t } = useI18n();

const route = useRoute();
const router = useRouter();

type ObjectTypeParam = keyof typeof reverseObjectTypeQueryMapping;
type CategoryParam = keyof typeof reverseCategoryQueryMapping;

const filterTitleObjectType = ref<StreamingChartObjectType>(
	reverseObjectTypeQueryMapping[route.query.t as ObjectTypeParam] ?? StreamingChartObjectType.Movie
);

const filterCountry = ref<WebLocale | null>(route.query.c === 'global' ? null : languageStore.webLocale);

const filterCategory = ref<PopularityRankTypes | undefined>(
	reverseCategoryQueryMapping[route.query.ct as CategoryParam] ?? PopularityRankTypes.WeeklyPopularitySameContentType
);

const filterStartDate = ref<string | null>(route.query.d?.toString() ?? null);

const queryVariables = computed(() => {
	let countryStreamingCharts = filterCountry.value?.toUpperCase() ?? null;
	if (countryStreamingCharts === 'UK') countryStreamingCharts = 'GB';

	return {
		countryStreamingCharts,
		withOffers: true,
		first: 10,
		after: '',
		filter: {
			objectType: filterTitleObjectType.value,
			category: filterCategory.value,
			startDate: filterStartDate.value,
		},
	};
});

type StreamingChartTitle = NonNullable<GetStreamingChartInfoQuery['streamingCharts']['edges']>[number];
const { result, fetchMore, loading } = useStreamingCharts(queryVariables);

const streamingChartData = computed(() => result.value?.streamingCharts);
const streamingChartTitles = computed(() => streamingChartData.value?.edges ?? []);
const firstTitle = computed(() => streamingChartTitles.value?.[0]);

const logoSrc = {
	png: `/${ASSETS_DIR}/img/logo/JustWatch-logo-small.png`,
	webp: `/${ASSETS_DIR}/img/logo/JustWatch-logo-small.webp`,
};

const selectFiltersObjectTypes = computed(() => [
	{ name: t('WEBAPP_MOVIES'), value: StreamingChartObjectType.Movie },
	{ name: t('WEBAPP_TVSHOWS'), value: StreamingChartObjectType.Show },
	{ name: t('WEBAPP_SEASONS'), value: StreamingChartObjectType.ShowSeason },
]);

const allowedCountries = ['us', 'uk', 'de', 'fr', 'es', 'it', 'in', 'mx', 'au', 'br', 'ca', 'tr', 'ar', 'eg', 'pl'];

const selectFiltersCountries = computed(() => {
	const countries = languageStore.locales
		.filter(country => allowedCountries.includes(country.webLocale))
		.map(item => {
			const emoji = WEB_LOCALES_EMOJIS[item.webLocale];
			const name = item.country_names[languageStore.language] ?? item.country;

			return {
				name: emoji ? `${emoji} ${name}` : name,
				value: item.webLocale,
			};
		})
		.toSorted((a, b) => a.name.localeCompare(b.name));

	return [{ name: 'Global', value: null }, ...countries];
});

const selectFiltersCategories = computed(() => [
	{ name: t('WEBAPP_DAILY'), value: PopularityRankTypes.DailyPopularitySameContentType },
	{ name: t('WEBAPP_WEEKLY'), value: PopularityRankTypes.WeeklyPopularitySameContentType },
	{ name: t('WEBAPP_MONTHLY'), value: PopularityRankTypes.MonthlyPopularitySameContentType },
]);

function onLoadMore() {
	// Bot protection
	if (navigator.webdriver) return;

	fetchMore({
		variables: {
			after: streamingChartData?.value?.pageInfo.endCursor,
		},
		updateQuery(previousResult, { fetchMoreResult }) {
			return {
				streamingCharts: {
					__typename: previousResult.streamingCharts.__typename,
					edges: [
						...(previousResult.streamingCharts.edges ?? []),
						...(fetchMoreResult?.streamingCharts.edges ?? []),
					],
					pageInfo: {
						__typename: fetchMoreResult?.streamingCharts.pageInfo.__typename,
						startCursor: fetchMoreResult?.streamingCharts.pageInfo.startCursor ?? '',
						hasPreviousPage: fetchMoreResult?.streamingCharts.pageInfo.hasPreviousPage ?? false,
						hasNextPage: fetchMoreResult?.streamingCharts.pageInfo.hasNextPage ?? false,
						endCursor: fetchMoreResult?.streamingCharts.pageInfo.endCursor ?? '',
					},
					totalCount: fetchMoreResult?.streamingCharts.totalCount ?? 0,
				},
			};
		},
	});
}

function getHeroSpinningText(title: StreamingChartTitle) {
	const titleName =
		title?.node?.__typename === 'Season'
			? `"${title.node.show.content?.title}" (${title.node.content.title})`
			: `"${title.node.content.title}"`;

	if (title?.streamingChartInfo?.trend === ModuleContentTitleTrend.New)
		return t('WEBAPP_STREAMING_CHARTS_DEBUT', { title: titleName });

	if (title?.streamingChartInfo?.trend === ModuleContentTitleTrend.Stable)
		return t('WEBAPP_STREAMING_CHARTS_HOLD', { title: titleName });

	if (
		title?.streamingChartInfo?.trend === ModuleContentTitleTrend.Up &&
		title?.streamingChartInfo?.topRank === 1 &&
		title?.streamingChartInfo.daysInTop3 > 1
	)
		return t('WEBAPP_STREAMING_CHARTS_REGAIN', { title: titleName });

	return `${t('WEBAPP_STREAMING_CHARTS_SPIN', { title: titleName })} ${
		title?.streamingChartInfo?.trendDifference && title.streamingChartInfo?.trendDifference > 0
			? t('WEBAPP_STREAMING_CHARTS_SPIN_RANKCHANGE', {
					rankChange: `+${formatTrendDifference(title.streamingChartInfo?.trendDifference)}`,
			  })
			: ''
	}`;
}

function formatTrendDifference(trendDifference: number) {
	return Intl.NumberFormat(languageStore.language).format(trendDifference);
}

const objectTypeMapping = {
	MOVIE: 'movies',
	SHOW: 'shows',
	SHOW_SEASON: 'seasons',
};

function onObjectTypeChange(value: StreamingChartObjectType) {
	filterTitleObjectType.value = value;
	router.replace({ query: { ...route.query, t: objectTypeMapping[value] } });
}

const categoryMapping = {
	DAILY_POPULARITY_SAME_CONTENT_TYPE: 'daily',
	WEEKLY_POPULARITY_SAME_CONTENT_TYPE: 'weekly',
	MONTHLY_POPULARITY_SAME_CONTENT_TYPE: 'monthly',
};

function onCategoryChange(value: PopularityRankTypes | undefined) {
	filterCategory.value = value;
	// The date picker was removed, so a category no longer carries an associated date.
	// Reset the start date so the charts show the latest period for the selected category.
	filterStartDate.value = null;

	router
		.replace({
			query: {
				...route.query,
				ct: value ? categoryMapping[value] : undefined,
				d: undefined,
			},
		})
		.catch(() => {});
}

// Kept for when the StreamingChartsDatePicker is restored — pairs category + date selection.
// function onSelectionChange({ category, date }: { category: PopularityRankTypes | undefined; date: string | null }) {
// 	filterCategory.value = category;
// 	filterStartDate.value = date;
//
// 	router
// 		.replace({
// 			query: {
// 				...route.query,
// 				ct: category ? categoryMapping[category] : undefined,
// 				d: date ?? undefined,
// 			},
// 		})
// 		.catch(() => {});
// }

async function onCountryChange(value: WebLocale | null) {
	filterCountry.value = value;

	if (value == null) {
		router.replace({ query: { ...route.query, c: 'global' } });
		return;
	}

	const localisedStreamingChartsPage = routingStore.urlMetadata?.hrefLangTags.find(
		lt => lt.href.slice(1, 3) === value
	)?.href;

	if (!localisedStreamingChartsPage) return;

	useLanguageStore().setWebLocale(value);

	jw_changeLocale(languageStore.webLocale, routingStore.activeDomain);
	await router.push({ path: localisedStreamingChartsPage, query: { ...route.query, c: undefined } });
}

function share(title: StreamingChartTitle, method?: ShareMethod) {
	const shareUrl = window.location.href;
	const shareText = getHeroSpinningText(title).toString();

	switch (method) {
		case ShareMethod.FACEBOOK:
			safeOpen(
				new ClassicUrl('http://www.facebook.com/sharer/sharer.php').set('u', shareUrl).set('t', shareText)
			);
			break;
		case ShareMethod.TWITTER:
			safeOpen(new ClassicUrl('https://twitter.com/intent/tweet').set('url', shareUrl).set('text', shareText));
			break;
		default:
			if (navigator && navigator.clipboard) {
				navigator.clipboard.writeText(shareUrl);
				addToast({ message: t('WEBAPP_COPIED_TO_CLIPBOARD') as string });
			}
	}
}
</script>

<style lang="scss" scoped>
.content {
	background-color: #141000;
}

.streaming-charts-header {
	position: sticky;
	top: 0;
	z-index: 99;
	padding: 16px 0;
	backdrop-filter: blur(5px);
}

.logo {
	max-width: 1920px;
	width: 100%;
	margin: auto;
	padding: 16px 48px;
	padding-top: 0;
	display: flex;
	align-items: center;
	justify-content: space-between;
	height: fit-content;

	@media (max-width: 700px) {
		padding: 16px;
		padding-top: 0;
	}

	img {
		max-height: 20px;
		max-width: 100%;
		height: auto;
	}

	&__title {
		flex: 1 1 auto;
		white-space: nowrap;
		font-size: 24px;
		line-height: 16px;
		letter-spacing: 0.36px;
		color: #796301;
		margin-left: 8px;
		transform: translateY(1px);

		@media (max-width: 315px) {
			font-size: 16px;
		}
	}
}

.nav {
	position: sticky;
	top: 16px;
	z-index: 99;
	padding: 0 48px;
	display: flex;
	justify-content: space-between;
	max-width: 1920px;
	margin: 0 auto;

	@media (max-width: 700px) {
		flex-direction: column-reverse;
		padding: 0 16px;
	}

	&__filterbar {
		display: flex;
		grid-gap: 8px;
		width: 100%;

		:deep(.hidden-horizontal-scrollbar) {
			width: 100%;
			margin: 0;
		}

		:deep(.hidden-horizontal-scrollbar__items),
		:deep(.hidden-horizontal-scrollbar__items--faded-end) {
			width: 100%;
			padding-left: 0;
			padding-right: 0;
		}
	}

	&__link {
		:deep(.basic-button) {
			background-color: #282100;
		}

		:deep(.basic-button__content) {
			color: #b8b8b8;
			font-size: 14px;
			font-weight: 900;
			line-height: 16px;
			letter-spacing: 0.21px;

			@media (max-width: 700px) {
				display: flex;
				justify-content: space-between;
				align-items: center;
			}
		}

		&__button {
			padding: 8px 16px;
			height: 40px;
			border-radius: 8px;
			font-size: 14px;
			font-weight: 900;
			line-height: 16px;
			letter-spacing: 0.21px;

			@media (max-width: 700px) {
				width: 100%;
				margin-bottom: 8px;
				text-align: start;
			}

			&__icon {
				color: white;
				margin-inline-start: 8px;
				transform: rotate(315deg);
			}
		}
	}
}

.charts {
	max-width: 1920px;
	margin: 0 auto;
	background: linear-gradient(90deg, #0a0a0a 0%, rgba(10, 10, 10, 0.24) 100%),
		linear-gradient(180deg, rgba(10, 10, 10, 0.64) 0%, #0a0a0a 100%);
	backdrop-filter: blur(100px);

	@media (max-width: 700px) {
		margin: initial;
	}

	&__top {
		background-color: #141000;
		padding: 0 48px;
		padding-bottom: 16px;
		margin-bottom: 16px;

		@media (max-width: 700px) {
			margin-top: -16px;
			padding: 0 16px;
			padding-bottom: 16px;
		}
	}

	&__text {
		padding: 64px 48px;
	}

	&__list {
		margin: 0 48px;
		padding-bottom: 64px;

		@media (max-width: 700px) {
			margin: 0 16px;
		}

		&__pagination {
			display: flex;
			justify-content: space-between;
			margin: 24px 0;
			color: #6f7d90;

			&__count {
				color: #6f7d90;
				font-size: 16px;
				letter-spacing: 0.24px;
				text-align: center;
			}

			&__button {
				border-radius: 8px;
				background: #292200;
				color: #c6c8cd;
				font-size: 16px;
				font-weight: 400;
				letter-spacing: 0.24px;
				padding: 8px;

				&:hover {
					background: #292200;
				}
			}
		}
	}

	&__form {
		padding: 24px 48px;
	}
}

.claim {
	display: flex;
	justify-content: space-between;
	margin: 40px 0;

	@media (max-width: 700px) {
		margin: 24px 0;
	}

	&__content {
		display: flex;
		flex-direction: column;
		align-items: flex-start;
		min-height: 100%;

		&__title {
			color: #e1b702;
			margin-top: -10px;

			-webkit-font-smoothing: antialiased;
			font-family: Anton, Anton-Fallback, Impact;
			font-size: 48px;
			line-height: 120%;
			letter-spacing: 0.4px;
			text-transform: uppercase;

			@media (max-width: 700px) {
				font-size: 38px;
			}
		}

		&__watch {
			display: flex;
			align-items: center;
			grid-gap: 8px;
			margin-top: auto;

			@media (max-width: 700px) {
				flex-direction: column;
				align-items: flex-start;
			}

			&__details {
				background: #292200;
				border-radius: 8px;
				border: none;
				padding: 9px 24px;
				color: #b8b8b8;
				font-size: 14px;
				font-weight: 700;
				letter-spacing: 0.24px;

				&:hover {
					cursor: pointer;
				}
			}
		}
	}

	&__poster {
		flex-shrink: 0;

		width: 176px;
		max-width: 176px;
		height: 100%;
		margin-inline-start: 128px;

		@media (max-width: 700px) {
			width: 72px;
			max-width: 72px;
			margin-inline-start: 16px;
		}
	}
}

.divider {
	width: 100%;
	height: 16px;
	display: flex;
	grid-gap: 12px;
	margin-bottom: 16px;

	&__title {
		color: #c6c8cd;
		white-space: nowrap;
		font-size: 16px;
		font-weight: 400;
		letter-spacing: 0.24px;
	}

	&__line {
		border-top: 2px solid #212121;
		width: 100%;
		transform: translateY(50%);
	}

	@media (max-width: 700px) {
		margin-top: 16px;
	}
}

.news {
	display: flex;
	flex-direction: column;
	color: #b8b8b8;
	font-size: 16px;
	font-weight: 400;
	letter-spacing: 0.24px;

	@media (max-width: 700px) {
		margin-top: -16px;
	}

	&__article {
		height: 48px;
		border-radius: 8px;
		background: #292200;
		display: flex;
		justify-content: space-between;
		align-items: center;
		flex-shrink: 0;

		&__title {
			color: #b8b8b8;
			font-size: 16px;
			letter-spacing: 0.24px;
		}
	}
}

.description {
	display: flex;
	flex-direction: row;
	grid-gap: 96px;

	padding: 0 32px 32px;
	margin: 48px 16px;

	@media (max-width: 700px) {
		flex-direction: column;
		grid-gap: 16px;
		padding: 0 16px 16px;
		margin: 48px 0;
	}

	&__main {
		color: #fff;
		font-size: 32px;
		font-weight: 900;
		line-height: 40px;
		letter-spacing: 0.32px;
	}

	&__text {
		color: #c6c8cd;
		font-size: 16px;
		font-weight: 400;
		line-height: 24px;
		letter-spacing: 0.24px;

		display: flex;
		flex-direction: column;
		justify-content: space-between;
		align-content: flex-start;

		@media (max-width: 700px) {
			grid-gap: 16px;
		}

		p {
			margin: 0;
		}

		&--link {
			color: #78a6b8;

			&:hover {
				text-decoration: underline;
				cursor: pointer;
			}
		}
	}
}

.share-buttons {
	display: flex;
	align-items: center;
	padding-top: 0;

	&__text {
		color: #6f7d90;
		font-size: 16px;
	}

	&__container {
		display: flex;
		flex-direction: row;
		justify-content: flex-end;
		font-size: 16px;
		margin-inline-start: 8px;
	}

	&__button {
		cursor: pointer;

		&:not(:first-of-type) {
			margin-inline-start: 8px;
		}
	}
}
</style>
